import { Link } from "react-router-dom";
import { Mail, MapPin, Linkedin } from "lucide-react";
import { Logo, TAGLINE } from "@/components/Logo";
import { COMPANY } from "@/lib/company";

const QUICK = [
  ["Home", "/"],
  ["Features", "/features"],
  ["Pricing", "/pricing"],
  ["Live demos", "/demo/solar-epc"],
  ["Book a demo", "/demo"],
  ["Blog", "/blog"],
  ["Careers", "/careers"],
  ["FAQ", "/faq"],
  ["Contact", "/contact"],
];

const LEGAL = [
  ["Privacy Policy", "/privacy-policy"],
  ["Terms of Service", "/terms-of-service"],
  ["Disclaimer", "/disclaimer"],
  ["Cookie Policy", "/cookie-policy"],
];

const INDUSTRIES_LINKS = [
  ["Solar EPC", "/industry/solar-epc"],
  ["Wind EPC", "/industry/wind-epc"],
  ["Engineering EPC", "/industry/engineering-epc"],
  ["Nuclear Projects", "/industry/nuclear-projects"],
  ["Thermal Power Plant", "/industry/thermal-power-plant"],
];

const OM_FOOTER = [
  ["O&M Platform", "/om"],
  ["O&M Dashboard", "/om/dashboard"],
  ["Asset Register", "/om/assets"],
  ["Daily Monitoring", "/om/daily-log"],
  ["Preventive Maintenance", "/om/preventive-maintenance"],
  ["Corrective Maintenance", "/om/corrective-maintenance"],
  ["Reliability Analytics", "/om/reliability"],
  ["Root Cause Analysis", "/om/rca"],
  ["O&M Reports", "/om/reports"],
  ["Sector Demo Pages", "/om/sectors"],
];

const Heading = ({ children }) => (
  <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#F97316]">{children}</h3>
);

const FootLink = ({ to, children, testId }) => (
  <Link
    to={to}
    className="block text-[12.5px] text-white/65 hover:text-[#F97316] transition-colors duration-150"
    data-testid={testId}
  >
    {children}
  </Link>
);

export default function Footer() {
  return (
    <footer className="border-t-2 border-[#F97316] bg-[#0A1628] text-white" data-testid="site-footer">
      <div className="max-w-[1200px] mx-auto px-5 sm:px-8 py-10 grid gap-9 sm:grid-cols-2 lg:grid-cols-6">
        <div>
          <Logo mono size="sm" className="text-white" />
          <p className="text-[12px] text-[#F97316] mt-3 font-medium">{TAGLINE}</p>
          <p className="text-[12.5px] text-white/60 mt-3 leading-relaxed">
            India's first AI-powered EPC project management and O&amp;M platform.
          </p>
          <p className="text-[12.5px] text-white/60 mt-3 leading-relaxed">
            Built by {COMPANY.legalName} for the engineers who deliver the country's power, refinery and
            infrastructure projects.
          </p>
          <a
            href="https://www.linkedin.com/company/sarthi-associates"
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-1.5 mt-4 text-[12px] text-white/65 hover:text-[#F97316] transition-colors duration-150"
            data-testid="footer-linkedin-link"
          >
            <Linkedin className="w-4 h-4" /> LinkedIn
          </a>
        </div>

        <div>
          <Heading>Quick Links</Heading>
          <div className="mt-3.5 space-y-2.5">
            {QUICK.map(([label, to]) => (
              <FootLink key={to} to={to} testId={`footer-link-${label.replace(/\s/g, "-").toLowerCase()}`}>{label}</FootLink>
            ))}
          </div>
        </div>

        <div>
          <Heading>Industries</Heading>
          <div className="mt-3.5 space-y-2.5">
            {INDUSTRIES_LINKS.map(([label, to]) => (
              <FootLink key={to} to={to} testId={`footer-industry-${to.split("/").pop()}`}>{label}</FootLink>
            ))}
          </div>
        </div>

        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-[0.16em] text-white">O&amp;M Module</h3>
          <div className="mt-3.5 space-y-2.5">
            {OM_FOOTER.map(([label, to]) => (
              <FootLink key={to} to={to} testId={`footer-om-${label.replace(/[^a-zA-Z]+/g, "-").toLowerCase()}`}>{label}</FootLink>
            ))}
          </div>
        </div>

        <div>
          <Heading>Legal</Heading>
          <div className="mt-3.5 space-y-2.5">
            {LEGAL.map(([label, to]) => (
              <FootLink key={to} to={to} testId={`footer-link-${to.slice(1)}`}>{label}</FootLink>
            ))}
          </div>
        </div>

        <div>
          <Heading>Contact Us</Heading>
          <div className="mt-3.5 space-y-3 text-[12.5px] text-white/65">
            <a
              href={`mailto:${COMPANY.email}`}
              className="flex items-start gap-2 hover:text-[#F97316] transition-colors duration-150"
              data-testid="footer-email"
            >
              <Mail className="w-4 h-4 mt-0.5 shrink-0" /> {COMPANY.email}
            </a>
            <div className="flex items-start gap-2" data-testid="footer-address">
              <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
              <span>
                Registered office
                <br />
                {COMPANY.address}
              </span>
            </div>
            <p className="text-white/45 text-[11.5px] leading-relaxed">
              Support is handled over email, Monday to Saturday, 9:30 AM – 6:30 PM IST.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <p className="max-w-[1200px] mx-auto px-5 sm:px-8 py-5 text-center text-[11.5px] text-white/50" data-testid="footer-copyright">
          © 2025–{new Date().getFullYear()} Plan-Sarthi by {COMPANY.legalName}. All Rights Reserved.
        </p>
      </div>
    </footer>
  );
}
